const React = require('react')
const { useState, useEffect } = require('react')
const { useHistory } = require('react-router-dom')
const { Table, Button, Col, Row, UncontrolledTooltip } = require('reactstrap')
const { useNetworkConfig } = require('@contexts')
const {
    useBlockchainData,
    useFormatBytes,
    useFormatTimestampShort,
    useFormatTransactionCount,
    useResponsive,
    BREAKPOINTS
} = require('@hooks')
const { LoadingSpinner, CustomCard, ColorIcon } = require('@components')
const { Cube, FileText } = require('@phosphor-icons/react')

require('@styles/pages/home/latestTable.home.css')

const PropTypes = require('prop-types')

const LatestTable = props => {
    const { tableTitle, tableType } = props

    const history = useHistory()
    const { network } = useNetworkConfig()
    const [data, setData] = useState()
    const isMobile = useResponsive(BREAKPOINTS.large)

    const [loading, setLoading] = useState(true)
    const { getBlocks, getTransactions } = useBlockchainData(network.name)
    const formatBytes = useFormatBytes()
    const formatTimestamp = useFormatTimestampShort()
    const formatTransactionCount = useFormatTransactionCount()

    const isBlockTable = tableTitle.toLowerCase() === 'blocks'
    const isTransactionTable = tableTitle.toLowerCase() === 'transactions'

    useEffect(() => {
        const fetchData = async () => {
            let response

            try {
                if (isBlockTable) {
                    response = await getBlocks(1, 6)
                } else if (isTransactionTable) {
                    response = await getTransactions(1, 5)
                }
                if (response) {
                    setData(response.items)
                }
            } catch (error) {
                console.error('Error fetching data:', error)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [])

    const getBlockTime = currentIndex => {
        if (!data || currentIndex >= data.length - 1) return 0
        const currentBlock = data[currentIndex]
        const nextBlock = data[currentIndex + 1]
        if (!currentBlock?.timestamp || !nextBlock?.timestamp) return 0

        return Math.abs(parseInt(currentBlock.timestamp) - parseInt(nextBlock.timestamp))
    }

    return (
        <CustomCard
            style={{
                marginRight: isBlockTable && !isMobile && '15px',
                marginLeft: !isBlockTable && !isMobile && '15px'
            }}
            className={`box table-info`}
        >
            <Row className='table-header'>
                <Col>
                    <span className='table-title' style={{ fontSize: isMobile ? '16px' : '18px' }}>
                        Latest {tableTitle}
                    </span>
                </Col>
                <Col className='d-flex justify-content-end'>
                    <Button
                        className={`view-all-btn ${network.name}-border-color ${network.name}-btn`}
                        outline
                        onClick={() => history.push(`/${network.name}/${tableType}`)}
                    >
                        <span className={`${network.name}-text-color`}>View all</span>
                    </Button>
                </Col>
            </Row>
            <Table responsive>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={2} className='text-center py-4'>
                                <LoadingSpinner />
                            </td>
                        </tr>
                    ) : (
                        data &&
                        data?.slice(0, 5).map((item, index) => {
                            if (isBlockTable) {
                                const blockTime = getBlockTime(index)

                                return (
                                    <tr key={item.number}>
                                        <th>
                                            <span className='icon-wrapper'>
                                                <ColorIcon
                                                    icon={
                                                        <Cube
                                                            weight='bold'
                                                            size={32}
                                                            style={{ fill: 'url(#gradient)' }}
                                                        />
                                                    }
                                                />
                                            </span>
                                        </th>
                                        <td>
                                            <Row
                                                style={{
                                                    alignItems: 'center',
                                                    lineHeight: '25px',
                                                    overflowX: 'auto',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                <Col xs={4}>
                                                    <span
                                                        className={`${network.name}-text-color cursor-pointer`}
                                                        onClick={() =>
                                                            history.push(`/${network.name}/block/${item.number}`)
                                                        }
                                                        style={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        {item.number}
                                                    </span>
                                                </Col>
                                                <Col xs={8} className='address'>
                                                    Validator
                                                    <span
                                                        id={`validator-${item.number}`}
                                                        className={`${network.name}-text-color cursor-pointer`}
                                                        onClick={() =>
                                                            history.push(`/${network.name}/address/${item.validator}`)
                                                        }
                                                        style={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        &nbsp;
                                                        {formatBytes(item.validator, 30, 10, 9)}
                                                    </span>
                                                    <UncontrolledTooltip
                                                        placement='top'
                                                        target={`validator-${item.number}`}
                                                    >
                                                        {item.validator}
                                                    </UncontrolledTooltip>
                                                </Col>
                                            </Row>
                                            <Row style={{ alignItems: 'center', lineHeight: '25px' }}>
                                                <Col xs={4} className='small-grey'>
                                                    {formatTimestamp(item.timestamp)}
                                                </Col>
                                                <Col xs={8} className='small-grey'>
                                                    {item.transactionCount === 0 ? (
                                                        <span>{formatTransactionCount(item.transactionCount)}</span>
                                                    ) : (
                                                        <span
                                                            className={`${network.name}-text-color cursor-pointer`}
                                                            onClick={() =>
                                                                history.push(
                                                                    `/${network.name}/txs?block=${item.number}`
                                                                )
                                                            }
                                                        >
                                                            {formatTransactionCount(item.transactionCount)}
                                                        </span>
                                                    )}
                                                    &nbsp;in {blockTime} secs
                                                </Col>
                                            </Row>
                                        </td>
                                    </tr>
                                )
                            } else if (isTransactionTable) {
                                return (
                                    <tr key={item.hash}>
                                        <th>
                                            <span className='icon-wrapper'>
                                                <ColorIcon
                                                    icon={
                                                        <FileText
                                                            weight='fill'
                                                            size={36}
                                                            style={{ fill: 'url(#gradient)' }}
                                                        />
                                                    }
                                                />
                                            </span>
                                        </th>
                                        <td>
                                            <Row style={{ alignItems: 'center', lineHeight: '25px' }}>
                                                <Col xs={4}>
                                                    <span
                                                        id={`transaction-hash-${item.hash}`}
                                                        className={`${network.name}-text-color cursor-pointer`}
                                                        onClick={() => history.push(`/${network.name}/tx/${item.hash}`)}
                                                    >
                                                        {formatBytes(item.hash, 15, 12, 0)}
                                                    </span>
                                                    <UncontrolledTooltip
                                                        placement='top'
                                                        target={`transaction-hash-${item.hash}`}
                                                    >
                                                        {item.hash}
                                                    </UncontrolledTooltip>
                                                </Col>
                                                <Col
                                                    xs={4}
                                                    className='address'
                                                    style={
                                                        isMobile
                                                            ? {
                                                                  overflowX: 'auto',
                                                                  whiteSpace: 'nowrap'
                                                              }
                                                            : {}
                                                    }
                                                >
                                                    From
                                                    <span
                                                        id={`from-${item.hash}`}
                                                        className={`${network.name}-text-color cursor-pointer`}
                                                        onClick={() =>
                                                            history.push(
                                                                `/${network.name}/address/${item.from.address}`
                                                            )
                                                        }
                                                    >
                                                        &nbsp;
                                                        {formatBytes(item.from.address, 30, 8, 7)}
                                                    </span>
                                                    <UncontrolledTooltip placement='top' target={`from-${item.hash}`}>
                                                        {item.from.address}
                                                    </UncontrolledTooltip>
                                                </Col>
                                                <Col xs={4}>
                                                    <div className={`badge`}>
                                                        <span
                                                            className={`${
                                                                item.status ? 'status-success' : 'status-fail'
                                                            }`}
                                                        >
                                                            {item.status ? 'Success' : 'Fail'}
                                                        </span>
                                                    </div>
                                                </Col>
                                            </Row>
                                            <Row style={{ alignItems: 'center', lineHeight: '25px' }}>
                                                <Col xs={4} className='small-grey'>
                                                    {formatTimestamp(item.blockTimestamp)}
                                                </Col>
                                                <Col
                                                    xs={4}
                                                    className='address'
                                                    style={
                                                        isMobile
                                                            ? {
                                                                  overflowX: 'auto',
                                                                  whiteSpace: 'nowrap'
                                                              }
                                                            : {}
                                                    }
                                                >
                                                    {item.contractAddress ? (
                                                        <>
                                                            Create:&nbsp;
                                                            <span
                                                                id={`to-${item.hash}`}
                                                                className={`${network.name}-text-color cursor-pointer`}
                                                                onClick={() =>
                                                                    history.push(
                                                                        `/${network.name}/address/${item.contractAddress}`
                                                                    )
                                                                }
                                                            >
                                                                {formatBytes(item.contractAddress, 30, 8, 7)}
                                                            </span>
                                                            <UncontrolledTooltip
                                                                placement='top'
                                                                target={`to-${item.hash}`}
                                                            >
                                                                {item.contractAddress}
                                                            </UncontrolledTooltip>
                                                        </>
                                                    ) : (
                                                        <>
                                                            To:&nbsp;
                                                            <span
                                                                id={`to-${item.hash}`}
                                                                className={`${network.name}-text-color cursor-pointer`}
                                                                onClick={() =>
                                                                    history.push(
                                                                        `/${network.name}/address/${item.to.address}`
                                                                    )
                                                                }
                                                            >
                                                                {formatBytes(item.to.address, 30, 8, 7)}
                                                            </span>
                                                            <UncontrolledTooltip
                                                                placement='top'
                                                                target={`to-${item.hash}`}
                                                            >
                                                                {item.to.address}
                                                            </UncontrolledTooltip>
                                                        </>
                                                    )}
                                                </Col>
                                                <Col xs={4}>
                                                    <div className={`badge`}>
                                                        <span
                                                            id={`function-name-${item.hash}`}
                                                            className='function-name'
                                                        >
                                                            {item.functionName}
                                                        </span>
                                                        <UncontrolledTooltip
                                                            placement='top'
                                                            target={`function-name-${item.hash}`}
                                                        >
                                                            {item.functionName}
                                                        </UncontrolledTooltip>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </td>
                                    </tr>
                                )
                            }
                        })
                    )}
                </tbody>
            </Table>
        </CustomCard>
    )
}

LatestTable.propTypes = {
    tableTitle: PropTypes.string.isRequired,
    tableType: PropTypes.string.isRequired
}

module.exports = { LatestTable: React.memo(LatestTable) }
